import { Label } from "@/components/ui/label";
import { Combobox } from "@/components/ui/combobox";
import { DeadlineField } from "@/components/tasks/DeadlineField";
import {
  FREQUENCY_OPTIONS,
  MAX_OCCURRENCES,
  WEEKDAY_OPTIONS,
  WEEK_OF_MONTH_OPTIONS,
  describeRecurrence,
  occurrenceDates,
  type RecurrenceFormState,
} from "@/lib/recurrence";
import type { RecurrenceFrequency } from "@/types";

/**
 * Sarjan asetukset — the recurrence rule controls, shared by Luo uusi tehtävä
 * and Muokkaa tehtävän tietoja.
 */
export function RecurrenceFields({
  value,
  onChange,
  startYmd,
  disabled = false,
}: {
  value: RecurrenceFormState;
  onChange: (next: RecurrenceFormState) => void;
  startYmd: string;
  disabled?: boolean;
}) {
  const needsWeekday = value.frequency !== "DAILY_WEEKDAYS";
  const needsWeekOfMonth = value.frequency === "MONTHLY_NTH_WEEKDAY";
  const preview = describeRecurrence(value, startYmd);
  const tooMany = occurrenceDates(value, startYmd).length > MAX_OCCURRENCES;

  return (
    <div className="grid gap-3 min-w-0 rounded-md border border-input p-3">
      <div className="grid gap-2 min-w-0">
        <Label className="text-xs text-muted-foreground">Toistuvuus</Label>
        <Combobox
          options={FREQUENCY_OPTIONS}
          value={value.frequency}
          onChange={(next) =>
            onChange({
              ...value,
              frequency: (next || "WEEKLY") as RecurrenceFrequency,
            })
          }
          placeholder="Valitse toistuvuus"
          searchPlaceholder="Hae toistuvuutta..."
          disabled={disabled}
          className="w-full min-w-0"
        />
      </div>

      {needsWeekOfMonth && (
        <div className="grid gap-2 min-w-0">
          <Label className="text-xs text-muted-foreground">
            Kuukauden viikko
          </Label>
          <Combobox
            options={WEEK_OF_MONTH_OPTIONS}
            value={String(value.weekOfMonth)}
            onChange={(next) =>
              onChange({ ...value, weekOfMonth: Number(next ?? 1) })
            }
            placeholder="Valitse viikko"
            searchPlaceholder="Hae viikkoa..."
            disabled={disabled}
            className="w-full min-w-0"
          />
        </div>
      )}

      {needsWeekday && (
        <div className="grid gap-2 min-w-0">
          <Label className="text-xs text-muted-foreground">Viikonpäivä</Label>
          <Combobox
            options={WEEKDAY_OPTIONS}
            value={String(value.weekday)}
            onChange={(next) => onChange({ ...value, weekday: Number(next ?? 0) })}
            placeholder="Valitse viikonpäivä"
            searchPlaceholder="Hae viikonpäivää..."
            disabled={disabled}
            className="w-full min-w-0"
          />
        </div>
      )}

      <div className="grid gap-2 min-w-0">
        <Label className="text-xs text-muted-foreground">
          Viimeinen toistuva tehtävä <span className="text-red-500">*</span>
        </Label>
        <DeadlineField
          value={value.endDate}
          onChange={(endDate) => onChange({ ...value, endDate })}
        />
      </div>

      {tooMany ? (
        <p className="text-xs text-red-600">
          Sääntö tuottaisi yli {MAX_OCCURRENCES} tehtävää. Lyhennä aikaväliä.
        </p>
      ) : (
        preview && <p className="text-xs text-muted-foreground">{preview}</p>
      )}
    </div>
  );
}
